import { useEffect, useMemo, useState } from 'react';
import {
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Info,
  Save,
  Search,
  UserCheck,
  UserMinus,
  XCircle,
} from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { EmptyState } from '@/components/ui/EmptyState';
import { Select } from '@/components/ui/Select';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { cn, getInitials } from '@/lib/utils';
import type {
  AttendanceRegister,
  AttendanceStatus,
  RegisterEntryPayload,
  RegisterStudent,
} from '@/types/attendance';
import { teacherApi } from '../../api/teacher.api';
import { getAvatarColor } from '../../utils/avatarColor';
import { RowsPerPageSelect } from '@/components/ui/RowsPerPageSelect';

interface StudentAttendanceRegisterProps {
  /**
   * The register itself, owned by the section above.
   *
   * Passed in rather than fetched here because the stat cards beside this table
   * are counted from the very same rows — fetching it twice was two identical
   * requests for a whole roster on every change of class or date, and two
   * copies free to answer differently while one of them was still in flight
   * (CLAUDE.md §3).
   */
  register: {
    data: AttendanceRegister | null;
    isLoading: boolean;
    error: { message: string } | null;
    refetch: () => void;
  };
}

/**
 * The screen's working copy of the register.
 *
 * For a teacher who can mark, an unmarked child starts as Present — the way a
 * register is taken: change the few absentees, then save. Nothing is stored
 * until they press Save, so the default is theirs to confirm.
 *
 * For anyone who can only read it, `null` stays `null`: a register nobody has
 * taken is shown as not marked, never as a class full of "present" no teacher
 * gave.
 */
type Draft = Record<string, AttendanceStatus | null>;

function draftFrom(students: RegisterStudent[], canMark: boolean): Draft {
  return Object.fromEntries(
    students.map((s) => [s.uuid, s.status ?? (canMark ? 'PRESENT' : null)]),
  );
}

/** What the register's status filter can hold — the select's options are exactly these. */
type StatusFilter = 'ALL' | AttendanceStatus | 'UNMARKED';

function initialsOf(fullName: string) {
  const [first = '', ...rest] = fullName.split(' ').filter(Boolean);
  return getInitials(first, rest.at(-1) ?? first);
}

function formatDay(day: string) {
  return new Date(`${day}T00:00:00.000Z`).toLocaleDateString('en-IN', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function matches(student: RegisterStudent, needle: string) {
  return (
    student.fullName.toLowerCase().includes(needle) ||
    student.admissionNumber.toLowerCase().includes(needle) ||
    (student.rollNumber ?? '').toLowerCase().includes(needle)
  );
}

export function StudentAttendanceRegister({ register }: StudentAttendanceRegisterProps) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { data, isLoading, error, refetch } = register;

  const registerKey = data ? `${data.section.uuid}:${data.date}` : null;
  useEffect(() => {
    if (data) setDraft(draftFrom(data.students, data.canMark));
    setSelected([]);
    setSearch('');
    setPage(1);
  }, [registerKey, data]);

  const students = data?.students ?? [];
  const isReadOnly = Boolean(data && !data.canMark);

  // Filter by search text and status
  const visible = useMemo(() => {
    let result = students;
    const needle = search.trim().toLowerCase();
    if (needle) {
      result = result.filter((student) => matches(student, needle));
    }
    if (statusFilter !== 'ALL') {
      result = result.filter((student) => {
        const s = draft ? (draft[student.uuid] ?? null) : student.status;
        if (statusFilter === 'UNMARKED') return s === null;
        return s === statusFilter;
      });
    }
    return result;
  }, [students, search, statusFilter, draft]);

  // Pagination slicing
  const totalPages = Math.max(Math.ceil(visible.length / pageSize), 1);
  const paginatedStudents = useMemo(() => {
    const start = (page - 1) * pageSize;
    return visible.slice(start, start + pageSize);
  }, [visible, page, pageSize]);

  const pendingChanges = useMemo(
    () => students.filter((student) => (draft?.[student.uuid] ?? null) !== student.status).length,
    [students, draft],
  );

  function toggleSelected(studentUuid: string) {
    setSelected((previous) =>
      previous.includes(studentUuid)
        ? previous.filter((uuid) => uuid !== studentUuid)
        : [...previous, studentUuid],
    );
  }

  const visibleUuids = useMemo(() => paginatedStudents.map((s) => s.uuid), [paginatedStudents]);
  const selectedVisibleCount = visibleUuids.filter((uuid) => selected.includes(uuid)).length;
  const areAllVisibleSelected = visibleUuids.length > 0 && selectedVisibleCount === visibleUuids.length;

  function toggleAllVisible() {
    setSelected((previous) =>
      areAllVisibleSelected
        ? previous.filter((uuid) => !visibleUuids.includes(uuid))
        : [...new Set([...previous, ...visibleUuids])],
    );
  }

  function setStudentStatus(studentUuid: string, status: AttendanceStatus) {
    if (isReadOnly) return;
    setDraft((prev) => ({
      ...(prev ?? {}),
      [studentUuid]: status,
    }));
  }

  function applyToClass(status: Extract<AttendanceStatus, 'PRESENT' | 'ABSENT'>) {
    const chosen = new Set(selected.length > 0 ? selected : visible.map((s) => s.uuid));
    const rest = status === 'PRESENT' ? 'ABSENT' : 'PRESENT';

    // A child on leave stays on leave: the leave was granted on purpose, often
    // from an approved request, and a whole-class button is not the teacher
    // deciding otherwise for that one child.
    setDraft((prev) =>
      Object.fromEntries(
        students.map((s) => {
          const current = prev ? (prev[s.uuid] ?? null) : s.status;
          if (current === 'LEAVE') return [s.uuid, current];
          return [s.uuid, chosen.has(s.uuid) ? status : rest];
        }),
      ),
    );
    setSelected([]);
    setSearch('');
  }

  async function handleSave() {
    if (!data || !draft) return;

    const entries: RegisterEntryPayload[] = students.flatMap((student) => {
      const status = draft[student.uuid] ?? null;
      return status ? [{ studentUuid: student.uuid, status }] : [];
    });

    if (entries.length === 0) return;
    setIsSaving(true);

    try {
      const saved = await teacherApi.saveRegister(data.section.uuid, data.date, entries);
      setDraft(draftFrom(saved.students, saved.canMark));
      toast.success(
        `Attendance saved for ${saved.section.displayName}`,
        `${saved.summary.present} present · ${saved.summary.absent} absent on ${formatDay(saved.date)}.`,
      );
      refetch();
    } catch (caught) {
      toast.error(
        'Could not save the register',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {isLoading && <SkeletonLines count={8} />}

      {!isLoading && error && (
        <div className="card flex flex-col items-start gap-3 p-5">
          <Alert tone="critical" title="Could not load this register">
            {error.message}
          </Alert>
          <Button variant="secondary" size="sm" onClick={refetch}>
            Try again
          </Button>
        </div>
      )}

      {/* A subject teacher may look at a class's register but only its class
          teacher keeps it — said once, above everything else, rather than
          leaving them to discover the status buttons do nothing. A note, not a
          message: it is how this register is, not something that happened. */}
      {!isLoading && !error && isReadOnly && (
        <Alert tone="info" variant="note" title="View only">
          You can see this class's attendance. Only its class teacher, or the school office, can
          mark it.
        </Alert>
      )}

      {!isLoading && !error && data && students.length === 0 && (
        <div className="card p-8">
          <EmptyState
            icon={<UserCheck className="h-5 w-5" />}
            title="No students on this roster yet"
            description="Once students are admitted into this section they appear here, ready to be marked."
          />
        </div>
      )}

      {!isLoading && !error && data && students.length > 0 && (
        <div className="card bg-surface border border-line rounded-[16px] overflow-hidden shadow-2xs flex flex-col">
          {/* --- TABLE HEADER & TOOLBAR (Single line with no wrapping) ------------ */}
          <div className="border-b border-line bg-surface-2/40 px-4 sm:px-5 py-3 sm:py-4 flex flex-col lg:flex-row lg:items-start justify-between gap-3">
            <div className="shrink-0">
              <h3 className="text-base font-bold text-ink tracking-tight">Student Attendance</h3>
              <p className="text-xs text-ink-3 font-medium mt-0.5">
                {isReadOnly ? 'Search or filter by status' : 'Click on status to change'}
              </p>
            </div>

            {/* Filter, Search & Actions */}
            <div className="flex flex-col sm:flex-row sm:flex-wrap lg:flex-nowrap items-stretch sm:items-start gap-2.5 w-full lg:w-auto">
              {/* Search input - full width on its own single row on mobile */}
              <div className="relative w-full sm:w-44 shrink-0">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-3" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Search students..."
                  className="h-8 w-full pl-8 pr-2.5 text-sm bg-surface border border-line rounded-field text-ink placeholder:text-ink-3 focus:outline-none focus:border-emerald-500"
                />
              </div>

              {/* Status Filter & Action buttons row */}
              <div className="flex flex-wrap items-start gap-2">
                {/* Status Filter dropdown */}
                <div className="w-32 sm:w-30 shrink-0">
                  <Select
                    id="attendance-status-filter"
                    label="Filter by Status"
                    hideLabel
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value as StatusFilter);
                      setPage(1);
                    }}
                    options={[
                      { value: 'ALL', label: 'All Status' },
                      { value: 'PRESENT', label: 'Present' },
                      { value: 'ABSENT', label: 'Absent' },
                      { value: 'LATE', label: 'Late' },
                      { value: 'LEAVE', label: 'Leave' },
                      { value: 'UNMARKED', label: 'Not Marked' },
                    ]}
                    className="h-8 text-xs font-semibold"
                  />
                </div>

                {/* Quick action buttons */}
                {!isReadOnly && (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<UserMinus className="h-3.5 w-3.5 text-rose-500" />}
                      className="h-8 px-2.5 text-sm font-bold whitespace-nowrap"
                      onClick={() => applyToClass('ABSENT')}
                      title="Set ticked (or all) students absent, rest present — anyone on leave stays on leave"
                    >
                      Mark Absent
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<UserCheck className="h-3.5 w-3.5 text-emerald-500" />}
                      className="h-8 px-2.5 text-sm font-bold whitespace-nowrap"
                      onClick={() => applyToClass('PRESENT')}
                      title="Set ticked (or all) students present, rest absent — anyone on leave stays on leave"
                    >
                      Mark Present
                    </Button>
                  </>
                )}

                {/* Save Attendance Button */}
                {!isReadOnly && (
                  <div className="flex flex-col items-end gap-1">
                    <Button
                      variant="primary"
                      size="sm"
                      leftIcon={<Save className="h-4 w-4" />}
                      isLoading={isSaving}
                      disabled={pendingChanges === 0}
                      onClick={() => void handleSave()}
                      className="h-8 px-3 text-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs whitespace-nowrap"
                    >
                      Save Attendance
                    </Button>
                    <span className="text-[11px] text-ink-3 font-medium tracking-tight whitespace-nowrap">
                      Last attendance: {data?.lastAttendanceDate ? formatDay(data.lastAttendanceDate) : 'Not taken yet'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* --- STUDENT ROSTER TABLE ------------------------------------------ */}
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-sm">
              <thead>
                <tr className="border-b border-line bg-surface-2/60 text-slate-700 dark:text-slate-300 font-bold tracking-wide text-[13px]">
                  <th className="py-3 px-4 w-10 text-center">
                    {!isReadOnly && (
                      <Checkbox
                        label="Select all"
                        hideLabel
                        checked={areAllVisibleSelected}
                        indeterminate={selectedVisibleCount > 0 && !areAllVisibleSelected}
                        onChange={toggleAllVisible}
                      />
                    )}
                  </th>
                  <th className="py-3 px-3 w-10 text-center text-ink-3">#</th>
                  <th className="py-3 px-4 w-60">Student Name</th>
                  <th className="py-3 px-4 w-24 text-center whitespace-nowrap">Roll No.</th>
                  <th className="py-3 px-4 w-72 text-center whitespace-nowrap">
                    <span className="inline-flex items-center gap-1">
                      Status <Info className="h-3.5 w-3.5 text-ink-3" />
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {paginatedStudents.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-10 text-center text-ink-3 text-sm font-medium">
                      Nobody on this register matches your search or filter.
                    </td>
                  </tr>
                ) : (
                  paginatedStudents.map((student, idx) => {
                    const status = draft ? (draft[student.uuid] ?? null) : student.status;
                    const isTicked = selected.includes(student.uuid);
                    const globalIdx = (page - 1) * pageSize + idx + 1;
                    const roll = student.rollNumber || '—';
                    const initials = initialsOf(student.fullName);
                    const avatarColorClass = getAvatarColor(student.uuid || student.fullName);

                    return (
                      <tr
                        key={student.uuid}
                        className={cn(
                          'hover:bg-surface-2/40 transition-colors',
                          isTicked && 'bg-emerald-500/5',
                        )}
                      >
                        {/* Checkbox */}
                        <td className="py-3 px-4 text-center">
                          {!isReadOnly && (
                            <Checkbox
                              label={`Select ${student.fullName}`}
                              hideLabel
                              checked={isTicked}
                              onChange={() => toggleSelected(student.uuid)}
                            />
                          )}
                        </td>

                        {/* Serial # */}
                        <td className="py-3 px-3 text-center font-semibold text-ink-3 text-xs">
                          {globalIdx}
                        </td>

                        {/* Name + Avatar */}
                        <td className="py-3 px-4 w-60">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <span
                              className={cn(
                                'grid h-8 w-8 shrink-0 place-items-center rounded-full font-bold text-xs border shadow-2xs transition-transform',
                                avatarColorClass,
                              )}
                            >
                              {initials}
                            </span>
                            <span
                              className="font-bold text-ink text-sm truncate"
                              title={student.fullName}
                            >
                              {student.fullName}
                            </span>
                          </div>
                        </td>

                        {/* Roll Number (Single Line, centered, compact) */}
                        <td className="py-3 px-4 w-24 text-center font-mono font-semibold text-ink-2 whitespace-nowrap text-xs">
                          {roll}
                        </td>

                        {/* Status Segmented Pill Buttons (Present, Absent, Late, Leave) */}
                        <td className="py-3 px-4 w-72 text-center">
                          <div className="inline-flex flex-wrap items-center justify-center gap-1.5">
                            {status === null && (
                              <span className="w-full text-xs font-semibold text-ink-3">
                                Not marked
                              </span>
                            )}
                            {/* Present Button */}
                            <button
                              type="button"
                              disabled={isReadOnly}
                              onClick={() => setStudentStatus(student.uuid, 'PRESENT')}
                              className={cn(
                                'px-2.5 py-1 rounded-[6px] text-sm font-bold transition-all flex items-center gap-1 border cursor-pointer',
                                status === 'PRESENT'
                                  ? 'border-emerald-500/50 bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-extrabold shadow-2xs'
                                  : 'border-line bg-surface text-ink-3 hover:text-ink hover:bg-surface-2',
                              )}
                            >
                              Present
                              {status === 'PRESENT' && <CheckCircle2 className="h-3.5 w-3.5" />}
                            </button>

                            {/* Absent Button */}
                            <button
                              type="button"
                              disabled={isReadOnly}
                              onClick={() => setStudentStatus(student.uuid, 'ABSENT')}
                              className={cn(
                                'px-2.5 py-1 rounded-[6px] text-sm font-bold transition-all flex items-center gap-1 border cursor-pointer',
                                status === 'ABSENT'
                                  ? 'border-rose-500/50 bg-rose-500 text-white font-extrabold shadow-2xs'
                                  : 'border-line bg-surface text-ink-3 hover:text-ink hover:bg-surface-2',
                              )}
                            >
                              Absent
                              {status === 'ABSENT' && <XCircle className="h-3.5 w-3.5" />}
                            </button>

                            {/* Late Button */}
                            <button
                              type="button"
                              disabled={isReadOnly}
                              onClick={() => setStudentStatus(student.uuid, 'LATE')}
                              className={cn(
                                'px-2.5 py-1 rounded-[6px] text-sm font-bold transition-all flex items-center gap-1 border cursor-pointer',
                                status === 'LATE'
                                  ? 'border-amber-500/50 bg-amber-500/20 text-amber-600 dark:text-amber-400 font-extrabold shadow-2xs'
                                  : 'border-line bg-surface text-ink-3 hover:text-ink hover:bg-surface-2',
                              )}
                            >
                              Late
                              {status === 'LATE' && <Clock className="h-3.5 w-3.5" />}
                            </button>

                            {/* Leave Button — the mark an approved leave request asks for */}
                            <button
                              type="button"
                              disabled={isReadOnly}
                              onClick={() => setStudentStatus(student.uuid, 'LEAVE')}
                              className={cn(
                                'px-2.5 py-1 rounded-[6px] text-sm font-bold transition-all flex items-center gap-1 border cursor-pointer',
                                status === 'LEAVE'
                                  ? 'border-violet-500 bg-violet-500 text-white font-extrabold shadow-2xs'
                                  : 'border-line bg-surface text-ink-3 hover:text-ink hover:bg-surface-2',
                              )}
                            >
                              Leave
                              {status === 'LEAVE' && <CalendarDays className="h-3.5 w-3.5" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* --- BOTTOM PAGINATION BAR ----------------------------------------- */}
          <div className="border-t border-line bg-surface-2/40 px-5 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
            <span className="text-ink-3 font-medium">
              Showing {(page - 1) * pageSize + 1} to{' '}
              {Math.min(page * pageSize, visible.length)} of {visible.length} students
            </span>

            <div className="flex items-center gap-4">
              <RowsPerPageSelect
                value={pageSize}
                options={[8, 10, 20, 50]}
                onChange={(rows) => {
                  setPageSize(rows);
                  setPage(1);
                }}
              />

              {/* Page buttons */}
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(p - 1, 1))}
                  className="grid h-7 w-7 place-items-center rounded-[6px] border border-line bg-surface text-ink hover:bg-surface-2 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPage(p)}
                    className={cn(
                      'grid h-7 w-7 place-items-center rounded-[6px] text-xs font-bold transition-colors',
                      p === page
                        ? 'bg-emerald-600 text-white shadow-xs'
                        : 'border border-line bg-surface text-ink hover:bg-surface-2',
                    )}
                  >
                    {p}
                  </button>
                ))}

                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                  className="grid h-7 w-7 place-items-center rounded-[6px] border border-line bg-surface text-ink hover:bg-surface-2 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
